/**
 * ContextModal provides a modal interface for selecting workspace, scenario, and origin
 * with internationalization support and URL-based language switching.
 *
 * Features:
 * - Three-level cascading selection (workspace → scenario → origin)
 * - i18n support with dynamic language switching
 * - URL-based language flag detection
 * - Controlled modal state management
 * - Validation for save button activation
 */

import React, { useState, FC, useMemo, useEffect } from "react";
import cx from "classnames";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Heading, Modal, Tag, Text } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { useUpdateUserPreferences } from "@common/hooks/useUpdateUserPreferences";
import changeContextMutation from "../../../graphql/changeContext.mutation.graphql";
import ScenariosSelect from "@common/layout/components/context-select/components/scenarios-select";
import OriginSelect from "@common/layout/components/context-select/components/origins-select";
import WorkspaceSelect from "@common/layout/components/context-select/components/workspace-select";
import ContextModalDetailsBox from "../context-modal-details-box";
import TourTooltipBeacon from "@common/components/tour-tooltip-beacon";
import styles from "./contextModal.module.scss";
import OnboardingVideoModal from "@common/components/onboarding-video-modal";
import {
  CONTEXT_MODAL_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";
import useContextUrl from "@common/hooks/useContextUrl";

/**
 * Represents a single select option value
 * @property {string} value - Unique identifier
 * @property {string} label - Display text
 * @property {any} self - Optional reference to the original object
 */
interface SelectValue {
  value: string;
  label: string;
  self?: any;
}

/**
 * Represents a select component's options and current value
 * @property {SelectValue} value - Currently selected value
 * @property {Array<SelectValue>} options - Available options
 */
interface SelectOptions {
  value: SelectValue;
  options: any;
}

interface ContextModalProps {
  open: boolean;
  canAccessEDH: boolean;
  workspace: SelectOptions;
  scenario: SelectOptions;
  origin: SelectOptions;
  onSave: (context: any, hasSameContext: boolean) => void;
  onClose: () => void;
}

/**
 * Modal component for context selection with cascading dropdowns
 * Manages workspace, scenario, and origin selection with i18n support
 *
 * @param {ContextModalProps} props - Component properties
 * @returns {JSX.Element} Modal with context selection interface
 */
const ContextModal: FC<ContextModalProps> = ({
  canAccessEDH,
  open: isOpen,
  scenario,
  origin,
  workspace,
  onSave,
  onClose,
}: ContextModalProps) => {
  const { workspace: urlWorkspace, scenario: urlScenario, origin: urlOrigin } = useContextUrl();
  const initialOrigin = origin?.value
    ? {
        ...origin?.value,
        self: {
          ...origin?.value?.self,
          scenario: scenario?.value?.self ?? null,
        },
      }
    : null;
  const [originValue, setOrigin] = useState(initialOrigin);
  const [workspaceValue, setWorkspace] = useState(null);
  const [scenarioValue, setScenario] = useState(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showProductive, setShowProductive] = useState<boolean>(false);
  const [saveContext, setSaveContext] = useState(false);
  const [isSavingContext, setIsSavingContext] = useState(false);
  const [showTourModal, setShowTourModal] = useState(false);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);

  const showHotspot = useMemo(() => {
    return shouldShowHotspot(CONTEXT_MODAL_ONBOARDING);
  }, [onboardingsSeen]);

  const [contextMutation] = useMutation(changeContextMutation);
  const { updateUserPreferences, isLoading: isSavingPreferences } = useUpdateUserPreferences();
  const { t } = useTranslation();

  const selectedWorkspace: SelectValue = useMemo(() => {
    if (!canAccessEDH || !!scenarioValue?.self) {
      return {
        value: scenarioValue?.self?.workspace?.id,
        label: scenarioValue?.self?.workspace?.name,
        self: scenarioValue?.self?.workspace,
      };
    }

    if (originValue?.self && "scenario" in originValue?.self) {
      return {
        value: originValue?.self?.scenario?.workspace?.id,
        label: originValue?.self?.scenario?.workspace?.name,
        self: originValue?.self?.scenario?.workspace,
      };
    }

    return workspace?.value;
  }, [canAccessEDH, scenarioValue, originValue, workspace]);

  const selectedScenario: SelectValue = useMemo(() => {
    if (!canAccessEDH) {
      return {
        value: scenarioValue?.self?.id,
        label: scenarioValue?.self?.name,
        self: scenarioValue?.self,
      };
    }

    if (originValue?.self && "scenario" in originValue?.self) {
      return {
        value: originValue?.self?.scenario?.id,
        label: originValue?.self?.scenario?.name,
        self: originValue?.self?.scenario,
      };
    }

    if (!!scenarioValue?.self) {
      return {
        value: scenarioValue?.self?.id,
        label: scenarioValue?.self?.name,
        self: scenarioValue?.self,
      };
    }

    return scenario?.value;
  }, [canAccessEDH, scenarioValue, originValue, scenario]);

  useEffect(() => {
    if (isOpen) {
      setWorkspace(null);
      setScenario(canAccessEDH || !scenario?.value ? null : scenario?.value);
      setOrigin(!canAccessEDH ? null : initialOrigin);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !showFilters) {
      setWorkspace(null);
      canAccessEDH && setScenario(null);
    }
  }, [showFilters]);

  const hasSameContext = useMemo(() => {
    return (
      urlWorkspace === selectedWorkspace?.value &&
      urlScenario === selectedScenario?.value &&
      urlOrigin === originValue?.value
    );
  }, [urlWorkspace, urlScenario, urlOrigin, selectedWorkspace, selectedScenario, originValue]);

  const handleSavePreferences = () => {
    if (isSavingPreferences || !saveContext) return;

    updateUserPreferences({
      savedContext: {
        workspaceId: selectedWorkspace?.value || null,
        scenarioId: selectedScenario?.value || null,
        originId: originValue?.value || null,
      },
    });
  };

  const handleClose = () => {
    setShowFilters(false);
    setWorkspace(null);
    canAccessEDH && setScenario(null);
    onClose?.();
  };

  const handleSaveContext = async () => {
    try {
      setIsSavingContext(true);

      await contextMutation({
        variables: {
          input: {
            workspaceId: selectedWorkspace?.value,
            scenarioId: selectedScenario?.value,
            originId: originValue?.value ?? undefined,
          },
        },
      });

      handleSavePreferences();
      onSave?.(
        {
          context: {
            workspace: selectedWorkspace,
            scenario: selectedScenario,
            origin: originValue,
          },
        },
        hasSameContext,
      );
      handleClose();
      setIsSavingContext(false);
    } catch (error) {
      setIsSavingContext(false);
      console.error("Error saving context:", error);
    }
  };

  // Enable save button only when all selections are made
  const activeSave = useMemo(() => {
    return (
      (canAccessEDH && !!(originValue?.value || scenarioValue?.value)) ||
      (!canAccessEDH && scenarioValue?.value)
    );
  }, [originValue, workspaceValue, scenarioValue, hasSameContext]);

  const handleToggleOnboarding = (isOpen: boolean) => {
    setShowTourModal(isOpen);
    setIsOnboardingOpen(isOpen, CONTEXT_MODAL_ONBOARDING);
  };

  return (
    <>
      {!showTourModal && (
        <Modal
          className={styles.contextModal}
          open={isOpen}
          onClose={handleClose}
          header={
            <div className={styles.contextModal_header}>
              <Heading type="h2" className={styles.headerTitle}>
                {t("context-modal.title")}

                {showHotspot && (
                  <button
                    className={styles.beaconButton}
                    onClick={() => handleToggleOnboarding(true)}
                  >
                    <TourTooltipBeacon />
                  </button>
                )}
              </Heading>

              <Text className={styles.headerSubtitle} weight="medium">
                {t("context-modal.subtitle")}
              </Text>
            </div>
          }
          actions={
            <Button
              disabled={!activeSave || isSavingContext || isSavingPreferences}
              loading={isSavingContext || isSavingPreferences}
              onClick={handleSaveContext}
            >
              {(canAccessEDH && !originValue?.value && !!scenarioValue?.value) || !canAccessEDH
                ? t("context-modal.btn-save-scenario")
                : t("context-modal.btn-save")}
            </Button>
          }
        >
          <div className={styles.contextModal_content}>
            {isOpen && (
              <ContextModalDetailsBox
                canAccessEDH={canAccessEDH && !!origin?.value?.self}
                origin={origin?.value?.self ?? null}
                hasOriginSelector={canAccessEDH}
                scenario={scenario?.value?.self}
                workspace={workspace?.value?.self}
              />
            )}

            <div
              className={cx(
                styles.filtersContainer,
                canAccessEDH && styles.filtersContainer__hasOrigin,
                showFilters && styles.filtersContainer__isOpen,
              )}
            >
              <Tag
                className={cx(styles.filtersContainer_tag)}
                onClick={() => setShowFilters(!showFilters)}
              >
                {canAccessEDH
                  ? t("context-modal.filters.filter-workspace-scenario")
                  : t("context-modal.filters.filter-workspace")}
              </Tag>

              {showFilters && (
                <div
                  className={cx(styles.filtersBox, canAccessEDH && styles.filtersBox__hasOrigin)}
                >
                  <button className={styles.filtersBox_close} onClick={() => setShowFilters(false)}>
                    <Icon name="close" size="md" color="neutral-70" />
                  </button>

                  <div
                    className={cx(
                      styles.filtersBox_selectors,
                      !canAccessEDH && styles.filtersBox_selectors__singleSelector,
                    )}
                  >
                    <WorkspaceSelect
                      isClearable
                      onChange={(value: any): void => {
                        setWorkspace(value);
                        setScenario(null);
                      }}
                    />

                    {canAccessEDH && (
                      <ScenariosSelect
                        workspaceValue={workspaceValue}
                        showAdvancedOptions
                        isClearable
                        onChange={(value: any): void => {
                          setScenario(value);
                          setOrigin(null);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.mainSelector}>
              {canAccessEDH ? (
                <OriginSelect
                  onlyProductive={showProductive || undefined}
                  workspaceValue={workspaceValue}
                  scenarioValue={scenarioValue}
                  originValue={originValue?.value ? originValue : null}
                  onChange={(value: any) => setOrigin(value)}
                />
              ) : (
                <ScenariosSelect
                  showOnlyProductive={showProductive}
                  workspaceValue={workspaceValue}
                  scenarioValue={scenarioValue}
                  showAdvancedOptions
                  onChange={(value: any): void => {
                    setScenario(value);
                    setOrigin(null);
                  }}
                />
              )}
            </div>

            <div className={styles.checkboxGroup}>
              <Checkbox
                labelRight={t("context-modal.filters.filter-productive", {
                  type: canAccessEDH
                    ? t("context-modal.origin")?.toLowerCase()
                    : t("context-modal.scenario")?.toLowerCase(),
                })}
                checked={showProductive}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setShowProductive(e.target.checked);
                }}
              />
              <Checkbox
                labelRight={t("context-modal.filters.save-context")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSaveContext(e.target.checked);
                }}
              />
            </div>
          </div>
        </Modal>
      )}

      {showHotspot && (
        <OnboardingVideoModal
          isOpen={showTourModal}
          title={t("onboarding.context-modal.title")}
          description={t("onboarding.context-modal.description")}
          videoUrl="https://files.foris.ai/darwin/onboarding/Nuevo+Selector+de+Contexto+-+PROD.mp4"
          onClose={() => handleToggleOnboarding(false)}
        />
      )}
    </>
  );
};

export default ContextModal;
